import { useEffect, useLayoutEffect, useRef, useState, type DependencyList } from 'react'
import { Link, useParams } from 'react-router-dom'
import { cmsLoaded, useCmsVersion } from '../cms/store'
import AuthorBox from '../components/AuthorBox'
import MediaImage from '../components/MediaImage'
import GoogleAd, { ADSENSE_SIDEBAR_SLOT } from '../components/GoogleAd'
import RecipeBody from '../components/RecipeBody'
import SectionHead from '../components/SectionHead'
import StoryCard from '../components/StoryCard'
import { categoryRoutes, formatImageCredit, getAuthor, getStory, loadFullStory, relatedStories, storiesByDate, type Story } from '../data/content'
import { renderArticleHtml } from '../lib/articleHtml'
import { authorPath, publicPath } from '../lib/paths'
import { SITE_ORIGIN } from '../seo/site'

const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

function useIsoLayoutEffect(effect: () => void | (() => void), deps: DependencyList) {
  useClientLayoutEffect(effect, deps)
}

export default function Article() {
  const { slug } = useParams()
  useCmsVersion()
  const [fetched, setFetched] = useState<{ slug: string; story: Story; related: Story[] } | null>(null)
  const [phase, setPhase] = useState<'ready' | 'loading' | 'missing'>('ready')
  const hydrated = useRef(false)
  const stored = slug ? getStory(slug) : undefined
  const fetchedStory = fetched && fetched.slug === slug ? fetched : null
  const story = fetchedStory ? fetchedStory.story : stored
  const related = fetchedStory ? fetchedStory.related : story ? relatedStories(story) : []

  useIsoLayoutEffect(() => {
    const firstPaint = !hydrated.current
    hydrated.current = true
    if (!slug) return
    const current = getStory(slug)
    if (current?.bodyHtml || current?.body?.length) return
    if (firstPaint && !current) return
    let cancelled = false
    setPhase('loading')
    loadFullStory(slug).then((full) => {
      if (cancelled) return
      if (!full) {
        setPhase('missing')
        return
      }
      setFetched({ slug, story: full.story, related: full.related })
      setPhase('ready')
    })
    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    document.title = story
      ? `${story.title} | TIGG`
      : phase === 'missing' || (cmsLoaded() && phase !== 'loading')
        ? 'Story not found | TIGG'
        : 'TIGG | The It Girl Guide'
    const description = story?.metaDescription || story?.dek
    if (!description) return
    let meta = document.querySelector('meta[name="description"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'description')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', description)
  }, [story, phase])

  if (!story && phase === 'loading') {
    return <main className="min-h-[40vh] py-24" aria-hidden="true" />
  }

  if (!story && (phase === 'missing' || cmsLoaded())) {
    return (
      <main className="py-24 text-center">
        <h1 className="font-serif text-4xl">Story not found</h1>
        <Link to={publicPath('/')} className="mt-6 inline-block text-[11px] tracking-[0.2em] uppercase hover:text-rose">
          Back to TIGG
        </Link>
      </main>
    )
  }

  if (!story) {
    return <main className="min-h-[40vh] py-24" aria-hidden="true" />
  }

  const categoryHref = categoryRoutes[story.category] ?? '/trends'
  const author = getAuthor(story.author)
  const bodyReady = Boolean(story.bodyHtml || story.body?.length)
  const bodyHtml = story.bodyHtml
    ? renderArticleHtml(
        story.bodyHtml,
        storiesByDate().map((item) => item.slug),
        story.slug,
      )
    : ''

  const meta = (
    <>
      <div className="text-caps flex items-center gap-3.5">
        <Link to={categoryHref} className="text-rose hover:text-ink">
          {story.category}
        </Link>
        <span className="text-muted">{story.date}</span>
      </div>
      <h1 className="text-caps-display m-0 text-balance text-ink">{story.title}</h1>
      {story.dek ? <p className="m-0 leading-relaxed text-copy">{story.dek}</p> : null}
      {author ? (
        <Link to={authorPath(author.slug)} className="text-caps self-start text-stone hover:text-rose">
          By {story.author}
        </Link>
      ) : (
        <p className="text-caps m-0 text-stone">By {story.author}</p>
      )}
    </>
  )

  return (
    <article>
      {story.image ? (
        <header className="grid min-w-0 grid-cols-1 gap-[clamp(20px,3.5vw,56px)] py-[clamp(28px,4.5vw,64px)] lg:min-h-[max(50svh,20rem)] lg:grid-cols-2 lg:items-stretch lg:gap-x-[clamp(32px,4vw,72px)] lg:py-4">
          <figure data-reveal className="relative m-0 h-full min-h-0">
            <div className="h-full min-h-[280px] overflow-hidden bg-sand aspect-[4/5] lg:absolute lg:inset-0 lg:min-h-0 lg:aspect-auto">
              <MediaImage src={story.image} size="hero" priority alt={story.imageAlt || story.title} className="block h-full w-full object-cover object-top" />
            </div>
            {story.imageCredit && (
              <figcaption className="absolute bottom-2 left-2 z-10 m-0 bg-ink px-2 py-[5px] text-[11px] leading-none text-white">
                {formatImageCredit(story.imageCredit)}
              </figcaption>
            )}
          </figure>
          <div data-reveal className="flex min-w-0 flex-col justify-center gap-3 lg:max-w-[38rem]">{meta}</div>
        </header>
      ) : (
        <header className="mx-auto max-w-[720px] py-[clamp(28px,4.5vw,64px)]">
          <div data-reveal className="flex max-w-[38ch] flex-col gap-5">{meta}</div>
        </header>
      )}

      <div
        className={`mx-auto mt-10 grid gap-10 pb-[clamp(28px,4.5vw,64px)] lg:grid-cols-[minmax(0,1fr)_300px] ${
          story.isRecipe ? 'max-w-[1280px]' : 'max-w-[980px]'
        }`}
      >
        <div className="min-w-0">
          {story.isRecipe && story.ingredients?.length && bodyHtml ? (
            <RecipeBody
              title={story.title}
              ingredients={story.ingredients}
              bodyHtml={bodyHtml}
              shareUrl={`${SITE_ORIGIN}/stories/${story.slug}/`}
            />
          ) : bodyHtml ? (
            <div
              data-reveal
              className="article-body text-copy [&_h2.wp-block-heading]:text-caps-h2 [&_h3.wp-block-heading]:text-caps-h3 [&_a]:text-rose [&_a]:underline [&_a]:underline-offset-2 [&_p]:mb-6"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : story.body?.length ? (
            <div data-reveal>
              {story.body.map((paragraph) => (
                <p key={paragraph.slice(0, 24)} className="mb-6 text-copy">
                  {paragraph}
                </p>
              ))}
            </div>
          ) : !bodyReady ? (
            <div className="min-h-48 bg-sand" aria-hidden="true" />
          ) : null}
          {author && <AuthorBox author={author} />}
          {story.faq?.length ? (
            <section className="mt-12 border-t border-rule pt-8" aria-labelledby="story-faq">
              <h2 id="story-faq" className="text-caps-h2 m-0 text-ink">
                FAQ
              </h2>
              <div className="mt-6 flex flex-col gap-6">
                {story.faq.map((item) => (
                  <div key={item.question}>
                    <h3 className="text-caps-h3 m-0 text-ink">{item.question}</h3>
                    <p className="mt-2 mb-0 text-copy">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
        <aside className="min-w-0" aria-label="Advertisement">
          <div className="min-h-[280px] bg-sand lg:sticky lg:top-24">
            <GoogleAd key={story.slug} slot={ADSENSE_SIDEBAR_SLOT} />
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-20 pb-[clamp(28px,4.5vw,64px)]">
          <SectionHead title="More like this" />
          <div className="grid grid-cols-1 gap-x-5 gap-y-8 min-[480px]:grid-cols-2 md:grid-cols-4">
            {related.map((item, index) => (
              <StoryCard key={item.slug} story={item} compact stagger={index} />
            ))}
          </div>
        </section>
      )}
    </article>
  )
}
